import { useEffect, useRef } from "react";
import { audioManager } from "../audio/AudioManager";
import { useAppStore } from "../store/useAppStore";
import { useProjectsCarouselStore } from "../store/useProjectsCarouselStore";

const buttonSelector = "button:not([data-audio-toggle='true']), a[href]:not([data-audio-nav='true']), [role='button']";
const backgroundMusicUrl = "/audio/music/Synths-INTRO-Music.mp3";

export function useAudioEvents() {
  const audioEnabled = useAppStore((state) => state.audioEnabled);
  const masterVolume = useAppStore((state) => state.masterVolume);
  const musicVolume = useAppStore((state) => state.musicVolume);
  const sfxVolume = useAppStore((state) => state.sfxVolume);
  const ambientVolume = useAppStore((state) => state.ambientVolume);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const reducedMotion = useAppStore((state) => state.reducedMotion);
  const scrollVelocity = useAppStore((state) => state.scrollVelocity);
  const activeProjectIndex = useProjectsCarouselStore((state) => state.activeIndex);
  const setAudioUnlocked = useAppStore((state) => state.setAudioUnlocked);
  const previousTransitionStateRef = useRef(isTransitioning);
  const previousProjectIndexRef = useRef(activeProjectIndex);
  const scrollFrameRef = useRef(0);

  useEffect(() => {
    audioManager.setMusicUrl(backgroundMusicUrl);
    audioManager.configure({
      ambientVolume,
      enabled: audioEnabled,
      masterVolume,
      musicVolume,
      sfxVolume
    });
    audioManager.setEnabled(audioEnabled);

    if (!audioEnabled) {
      setAudioUnlocked(false);
    }
  }, [ambientVolume, audioEnabled, masterVolume, musicVolume, setAudioUnlocked, sfxVolume]);

  useEffect(() => {
    if (!audioEnabled || audioManager.isUnlocked) {
      return;
    }

    const handleFirstGesture = () => {
      void audioManager.unlock().then((unlocked) => {
        if (unlocked) {
          setAudioUnlocked(true);
        }
      });
    };

    document.addEventListener("pointerdown", handleFirstGesture, { once: true, passive: true });
    document.addEventListener("keydown", handleFirstGesture, { once: true });
    return () => {
      document.removeEventListener("pointerdown", handleFirstGesture);
      document.removeEventListener("keydown", handleFirstGesture);
    };
  }, [audioEnabled, setAudioUnlocked]);

  useEffect(() => {
    const handlePointerUp = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest(buttonSelector)) {
        return;
      }

      audioManager.playSfx("button");
    };

    document.addEventListener("pointerup", handlePointerUp, { passive: true });
    return () => {
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);

  useEffect(() => {
    if (!previousTransitionStateRef.current && isTransitioning && !reducedMotion) {
      audioManager.playSfx("transition");
    }

    previousTransitionStateRef.current = isTransitioning;
  }, [isTransitioning, reducedMotion]);

  useEffect(() => {
    if (previousProjectIndexRef.current !== activeProjectIndex) {
      audioManager.playSfx("carousel");
    }

    previousProjectIndexRef.current = activeProjectIndex;
  }, [activeProjectIndex]);

  useEffect(() => {
    if (reducedMotion) {
      audioManager.setScrollIntensity(0);
      return;
    }

    if (scrollFrameRef.current) {
      return;
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = 0;
      audioManager.setScrollIntensity(scrollVelocity);
    });

    return () => {
      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = 0;
      }
    };
  }, [reducedMotion, scrollVelocity]);
}
